var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { AwesomeEvent, ComponentAPI } from '@philips-internal/oswald-ui-core';
import { ClickEventListener, PageEventListener, RenderEventListener, SelectItemEventListener, UnselectItemEventListener } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
class DeckAPI extends ComponentAPI {
    constructor() {
        super(...arguments);
        /** @event */
        this.onClick = new ClickEventListener();
        /** @event */
        this.onContextMenuClick = new ClickEventListener();
        /** @event */
        this.onDoubleClick = new ClickEventListener();
        /** @event */
        this.onDynamicContentRender = new RenderEventListener();
        /** @event */
        this.onNextPage = new PageEventListener();
        /** @event */
        this.onPrevPage = new PageEventListener();
        /** @event */
        this.onSelectGroup = new SelectItemEventListener();
        /** @event */
        this.onSelectItem = new SelectItemEventListener();
        /** @event */
        this.onUnselectGroup = new UnselectItemEventListener();
        /** @event */
        this.onUnselectItem = new UnselectItemEventListener();
    }
    getContext() {
        const { context } = this.state;
        if (!context) {
            throw Error('[Deck API]: Context not initialized.');
        }
        return context;
    }
    getCard(key) {
        const { data, keyField } = this.state;
        return data.find((card) => String(card[keyField]) === String(key));
    }
    /**
     * Gets the data of the deck.
     *
     * @returns the data of the deck.
     */
    getData() {
        return this.state.data;
    }
    /**
     * Sets the data of the deck.
     *
     * @param data - The data for the deck.
     */
    setData(data) {
        this.setState({ data });
    }
    /**
     * Gets the value of the done property.
     *
     * @returns a boolean indicating if there is more data to be loaded.
     */
    isDone() {
        return this.state.done;
    }
    /**
     * Defines if there is more data to be loaded.
     *
     * @param done - If the load more button should be visible.
     */
    setDone(done) {
        this.setState({ done });
    }
    /**
     * Gets the property used to group the cards.
     *
     * @returns the property used to group the cards.
     */
    getGroupBy() {
        return this.state.groupBy;
    }
    /**
     * Defines the property used to group the cards.
     *
     * @param groupBy - Property to group by.
     */
    setGroupBy(groupBy) {
        this.setState({ groupBy });
    }
    /**
     * Gets the current value of the lazy property.
     *
     * @returns a boolean indicating if the items are being lazy loaded.
     */
    isLazy() {
        return this.state.lazy;
    }
    /**
     * Defines if the items should be lazy loaded.
     *
     * @param lazy - Value for the lazy property.
     */
    setLazy(lazy) {
        this.setState({ lazy });
    }
    /**
     * Gets the current limit of items per page.
     *
     * @returns the current limit of items per page.
     */
    getLimit() {
        return this.state.limit;
    }
    /**
     * Defines the limit of items per page in the card listing.
     *
     * @param limit - The limit of items per page.
     */
    setLimit(limit) {
        this.setState({ limit });
    }
    /**
     * Gets the current text for the load more button.
     *
     * @returns the current text for the load more button.
     */
    getLoadMoreExpression() {
        return this.state.loadMoreExpression;
    }
    /**
     * Defines the text of the load more button.
     *
     * @param loadMoreExpression - Text for the load more button.
     */
    setLoadMoreExpression(loadMoreExpression) {
        this.setState({ loadMoreExpression });
    }
    /**
     * Defines the value of the loading property.
     *
     * @param loading  - Value of the loading property.
     */
    setLoading(loading) {
        this.setState({ loading });
    }
    /**
     * Get the current value of the loading property.
     *
     * @returns the current value of the loading property.
     */
    isLoading() {
        return this.state.loading;
    }
    /**
     * Returns if the items should be rendered inside a masonry layout.
     *
     * @returns a boolean indicating if the items should be rendered inside a masonry layout.
     */
    isMasonry() {
        return this.state.masonry;
    }
    /**
     * Defines if the items should be rendered inside a masonry layout.
     *
     * @param masonry - If the items should be rendered inside a masonry layout.
     */
    setMasonry(masonry) {
        this.setState({ masonry });
    }
    /**
    * Gets deck orientation.
    *
    * @returns the deck orientation.
    */
    getOrientation() {
        return this.state.orientation;
    }
    /**
    * Sets deck orientation.
    *
    * @param orientation - The deck horizontal or vertical orientation.
    */
    setOrientation(orientation) {
        this.setState({ orientation });
    }
    /**
    * Gets if the deck overflows its content and generate a inner scrollbar.
    *
    * @returns - If the deck overflows.
    */
    isOverflow() {
        return this.state.overflow;
    }
    /**
    * Sets if the deck overflows its content and generate a inner scrollbar.
    *
    * @param overflow - If the deck overflows.
    */
    setOverflow(overflow) {
        this.setState({ overflow });
    }
    /**
     * Trigger the fetch first page of the cards upon these conditions:
     * - "reset" parameter is true;
     * - Previous reset value is not true.
     *
     * @param reset - Value of the reset property.
     */
    setReset(reset) {
        this.setState({ reset });
    }
    /**
     * Get the current value of the reset property.
     *
     * @returns the current value of the reset property.
     */
    isReset() {
        return this.state.reset;
    }
    /**
     * Defines if the cards and groups should be selectable.
     *
     * @param selectable - If the cards and groups should be selectable.
     */
    setSelectable(selectable) {
        this.setState({ selectable });
    }
    /**
     * Get if the cards and groups are selectable.
     *
     * @returns a boolean indicating if the cards and groups are selectable.
     */
    isSelectable() {
        return this.state.selectable;
    }
    /**
     * Defines if the group should be collapsible.
     *
     * @param collapsible - If the group should be collapsible.
     */
    setCollapsible(collapsible) {
        this.setState({ collapsible });
    }
    /**
     * Get if the group is collapsible.
     *
     * @returns a boolean indicating if the group is collapsible.
     */
    isCollapsible() {
        return this.state.collapsible;
    }
    /**
     * Defines the selection type of the deck.
     *
     * @param selectionType - The selection type.
     */
    setSelectionType(selectionType) {
        this.setState({ selectionType });
    }
    /**
     * Get the current selection type.
     *
     * @returns the current selection type.
     */
    getSelectionType() {
        return this.state.selectionType;
    }
    /**
     * Defines the selection style of the card (only applies to single or single-group selection type).
     *
     * @param selectionStyle - the selection style of the card (only applies to single or single-group selection type).
     */
    setSelectionStyle(selectionStyle) {
        this.setState({ selectionStyle });
    }
    /**
     * Get the selection style.
     *
     * @returns the selection style value.
     */
    getSelectionStyle() {
        return this.state.selectionStyle;
    }
    /**
     * Get all the selected cards.
     *
     * @returns an array with all the selected cards.
     */
    getSelectedCards() {
        const { data, keyField, selectedCards } = this.state;
        return data.filter((card) => selectedCards.includes(String(card[keyField])));
    }
    /**
     * Get all the selected groups.
     *
     * @returns an array with all the selected groups.
     */
    getSelectedGroup() {
        const { data, groupBy, selectedGroups } = this.state;
        return data.filter((card) => selectedGroups.includes(card[groupBy]));
    }
    /**
     * Checks if the specified group is selected.
     *
     * @param groupKey - The key of the group.
     * @returns a boolean indicating if the group is selected.
     */
    isGroupSelected(groupKey) {
        const { selectedGroups = [] } = this.state;
        return this.getContext().isGroupSelected(groupKey, selectedGroups);
    }
    /**
     * Select the specified card by its key.
     *
     * @param key - The key of the card.
     */
    selectCard(key) {
        const internalKey = String(key);
        const { selectable, selectedCards = [], selectionType } = this.state;
        const { allowCardSelection, modifyCardSelection, isCardSelected } = this.getContext();
        const item = this.getCard(key);
        if (!isCardSelected(internalKey, selectedCards) && allowCardSelection(selectable, selectionType) && item) {
            const items = modifyCardSelection({
                item: internalKey,
                remove: false,
                selection: selectedCards,
                type: selectionType,
            });
            this.setState({
                selectedCards: items,
            });
            this.onSelectItem.fire({ item });
        }
    }
    /**
     * Select one or more cards by its keys.
     * If the selection type is set to multiple it will select the cards and override an existing selection;
     * If the selection type is set to simple you can use this method by passing an array with a single key.
     *
     * @param keys - The keys of one or more cards.
     * @throws an Error if the selection type is set to single and the array contains more than one key.
     */
    selectCards(keys) {
        const { selectable, selectionType } = this.state;
        const { allowCardSelection, isMultipleType } = this.getContext();
        if (allowCardSelection(selectable, selectionType)) {
            if (isMultipleType(selectionType) && keys.length > 1) {
                throw Error('[Deck] To select multiple cards the deck must have selectionType configured to multiple');
            }
            const selectedCards = [];
            for (const key of keys) {
                if (!this.getCard(key)) {
                    continue;
                }
                selectedCards.push(String(key));
            }
            this.setState({ selectedCards });
        }
    }
    /**
     * Unselect a specified card by its key.
     *
     * @param key - The key of the card.
     */
    unselectCard(key) {
        const { selectedCards = [], selectionType } = this.state;
        const item = this.getCard(key);
        const items = this.getContext().modifyCardSelection({
            item: String(key),
            remove: true,
            selection: selectedCards,
            type: selectionType,
        });
        if (items.length !== selectedCards.length && item) {
            this.setState({
                selectedCards: items,
            });
            this.onUnselectItem.fire({ item });
        }
    }
    /**
     * Select the specified group by its key.
     *
     * @param groupKey - The key of the group.
     */
    selectGroup(groupKey) {
        const { selectable, selectedGroups = [], selectionType } = this.state;
        const { allowGroupSelection, modifyGroupSelection } = this.getContext();
        if (allowGroupSelection(selectable, selectionType) && !this.isGroupSelected(groupKey)) {
            const newSelectedGroups = modifyGroupSelection({
                item: groupKey,
                remove: false,
                selection: selectedGroups,
                type: selectionType,
            });
            this.setState({
                selectedGroups: newSelectedGroups,
            });
            this.onSelectGroup.fire();
        }
    }
    /**
     * Unselect a specified group by its key.
     *
     * @param groupKey - The key of the group.
     */
    unselectGroup(groupKey) {
        const { selectedGroups = [], selectionType } = this.state;
        const newSelectedGroups = this.getContext().modifyGroupSelection({
            item: groupKey,
            remove: true,
            selection: selectedGroups,
            type: selectionType,
        });
        if (newSelectedGroups.length !== selectedGroups.length) {
            this.setState({
                selectedGroups: newSelectedGroups,
            });
            this.onUnselectGroup.fire();
        }
    }
    /**
     * Unselect all cards and groups.
     */
    unselectAll() {
        this.setState({ selectedCards: [], selectedGroups: [] });
    }
    /**
     * Defines the gap between the groups.
     *
     * @param groupGap - Value for the gap.
     */
    setGroupGap(groupGap) {
        this.setState({ groupGap });
    }
    /**
     * Get the current gap between the groups.
     *
     * @returns the value of the gap between the groups.
     */
    getGroupGap() {
        return this.state.groupGap;
    }
    /**
     * Defines the margin for the content of the group.
     *
     * @param groupContentMargin - Value for the margin.
     */
    setGroupContentMargin(groupContentMargin) {
        this.setState({ groupContentMargin });
    }
    /**
     * Get the current margin of the group content.
     *
     * @returns the value of the margin of the group content.
     */
    getGroupContentMargin() {
        return this.state.groupContentMargin;
    }
    /**
     * Defines the padding for the content of the group.
     *
     * @param groupContentPadding - Value for the padding.
     */
    setGroupContentPadding(groupContentPadding) {
        this.setState({ groupContentPadding });
    }
    /**
     * Get the current padding of the group content.
     *
     * @returns the value of the padding of the group content.
     */
    getGroupContentPadding() {
        return this.state.groupContentPadding;
    }
    /**
     * Defines the background color of the group.
     *
     * @param groupBackgroundColor - One of the available background colors in the enum.
     */
    setGroupBackgroundColor(groupBackgroundColor) {
        this.setState({ groupBackgroundColor });
    }
    /**
     * Get the current background color of the group.
     *
     * @returns the current background color of the group.
     */
    getGroupBackgroundColor() {
        return this.state.groupBackgroundColor;
    }
    /**
     * Get the current background color of the subgroup.
     *
     * @returns the current background color of the subgroup.
     */
    getSubGroupBackgroundColor() {
        return this.state.groupBackgroundColor;
    }
    /**
     * Defines the background color of the subgroup.
     *
     * @param subGroupBackgroundColor - One of the available background colors in the enum.
     */
    setSubGroupBackgroundColor(subGroupBackgroundColor) {
        this.setState({ subGroupBackgroundColor });
    }
    /**
     * Gets the property used to subgroup the cards.
     *
     * @returns the property used to subgroup the cards.
     */
    getSubGroupBy() {
        return this.state.subGroupBy;
    }
    /**
     * Defines the property used to subgroup the cards.
     *
     * @param subGroupBy - Property to subgroup by.
     */
    setSubGroupBy(subGroupBy) {
        this.setState({ subGroupBy });
    }
    /**
     * Defines the gap between the subgroups.
     *
     * @param subGroupGap - Value for the gap.
     */
    setSubGroupGap(subGroupGap) {
        this.setState({ subGroupGap });
    }
    /**
     * Get the current gap between the subgroups.
     *
     * @returns the value of the gap between the subgroups.
     */
    getSubGroupGap() {
        return this.state.subGroupGap;
    }
    /**
     * Defines the margin for the content of the subgroup.
     *
     * @param subGroupContentMargin - Value for the margin.
     */
    setSubGroupContentMargin(subGroupContentMargin) {
        this.setState({ subGroupContentMargin });
    }
    /**
     * Get the current margin of the subgroup content.
     *
     * @returns the value of the margin of the subgroup content.
     */
    getSubGroupContentMargin() {
        return this.state.subGroupContentMargin;
    }
    /**
     * Defines the padding for the content of the subgroup.
     *
     * @param subGroupContentPadding - Value for the padding.
     */
    setSubGroupContentPadding(subGroupContentPadding) {
        this.setState({ subGroupContentPadding });
    }
    /**
     * Get the current padding of the subgroup content.
     *
     * @returns the value of the padding of the subgroup content.
     */
    getSubGroupContentPadding() {
        return this.state.subGroupContentPadding;
    }
}
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DeckAPI.prototype, "onClick", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DeckAPI.prototype, "onContextMenuClick", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DeckAPI.prototype, "onDoubleClick", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DeckAPI.prototype, "onDynamicContentRender", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DeckAPI.prototype, "onNextPage", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DeckAPI.prototype, "onPrevPage", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DeckAPI.prototype, "onSelectGroup", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DeckAPI.prototype, "onSelectItem", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DeckAPI.prototype, "onUnselectGroup", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DeckAPI.prototype, "onUnselectItem", void 0);
export { DeckAPI, };
//# sourceMappingURL=deck-api.js.map